/* eslint-disable */
import { Box, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import DescriptionIcon from '@mui/icons-material/Description'
import { createElement, useEffect, useState } from 'react'
import { MenuItemLink, MenuProps, useResourceDefinitions, useSidebarState, useTranslate } from 'react-admin'
import { Dot } from '../components/Icons'
import useAutoLogout from '../hooks/useAutoLogout'
import SubMenu from './SubMenu'
import { border, color, xxllncColor } from './themes'

const Menu = ({ dense = false }: MenuProps): JSX.Element => {
  const resourcesDefinitions = useResourceDefinitions()
  const resources = Object.keys(resourcesDefinitions).map(name => resourcesDefinitions[name])
  const [open] = useSidebarState()
  const [state, setState] = useState({ menuSettings: false })
  const translate = useTranslate()

  useAutoLogout()

  const handleToggle = (menu: MenuName) => {
    setState(stateItem => ({ ...stateItem, [menu]: !stateItem[menu] }))
  }

  const getMenuItems = (object) => {
    let values = object.filter(resource => resource.icon && !resource.options?.submenu)
    const submenu = object.filter(resource => resource.icon && resource.options?.submenu)

    if (submenu.length > 0) {
      values = [
        ...values,
        {
          name: 'submenu.settings',
          resources: submenu.map(prop => prop.name),
          submenu: [...submenu]
        }
      ]
    }

    return values
  }

  return (
    <StyledBox mt={1} className={classes.root}>
      <div className={classes.spacer}>&nbsp;</div>
      {getMenuItems(resources).map(resource => {
        const items: JSX.Element[] = []

        if ('submenu' in resource) {
          items.push(
            <SubMenu
              key={resource.name}
              handleToggle={() => handleToggle('menuSettings')}
              isOpen={state.menuSettings}
              sidebarIsOpen={!open}
              name={resource.name}
              dense={dense}
            >
              {resource.submenu.map(item => (
                <StyledMenuItemLink
                  className={classes.link}
                  classes={classes}
                  dense={dense}
                  key={item.name}
                  leftIcon={<Dot fill='#fff' />}
                  primaryText={item.options?.label || item.name}
                  sidebarIsOpen={!open}
                  to={`/${item.name}`}
                />
              ))}
            </SubMenu>
          )
        } else {
          items.push(
            <StyledMenuItemLink
              className={classes.item}
              dense={dense}
              key={resource.name}
              primaryText={resource.options?.label || resource.name}
              sidebarIsOpen={!open}
              to={`/${resource.name}`}
              leftIcon={createElement(resource.icon)}
            />
          )

          // Insert verzoekenoverzicht after samenwerkingen
          if (resource.name === 'samenwerkingen') {
            items.push(
              <StyledMenuItemLink
                className={classes.item}
                dense={dense}
                key="verzoekenoverzicht"
                primaryText={translate('verzoekenoverzicht.menuLabel')}
                sidebarIsOpen={!open}
                to="/verzoekenoverzicht"
                leftIcon={<DescriptionIcon />}
              />
            )
          }
        }

        return items
      })}
    </StyledBox>
  )
}

type MenuName = 'menuSettings'

const PREFIX = 'xxllncMenu'

const classes = {
  root: `${PREFIX}-root`,
  link: `${PREFIX}-link`,
  branding: `${PREFIX}-branding`,
  logo: `${PREFIX}-logo`,
  lab: `${PREFIX}-lab`,
  active: `${PREFIX}-active`,
  icon: `${PREFIX}-icon`,
  item: `${PREFIX}-item`,
  spacer: `${PREFIX}-spacer`,
  footer: `${PREFIX}-footer`
}

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    overflow: 'hidden',
  },
  [`& .${classes.link}`]: {
    [theme.breakpoints.up('sm')]: {
      color: xxllncColor.light100,
      ['&:hover']: {
        backgroundColor: xxllncColor.dark100,
      },
    }
  },
  [`& .${classes.item}`]: {
    padding: '12px 13px',
    ['&:hover']: {
      [theme.breakpoints.up('sm')]: {
        backgroundColor: xxllncColor.light100,
        color: xxllncColor.dark100,
      }
    }
  },
  [`& .${classes.branding}`]: {
    display: 'flex',
    flexDirection: 'row',
  },
  [`& .${classes.logo}`]: {
    fill: color.common.inactive,
    width: theme.spacing(10),
    marginTop: theme.spacing(1.3),
    marginLeft: theme.spacing(2.5),
    marginRight: theme.spacing(1.5),
  },
  [`& .${classes.lab}`]: {
    backgroundColor: xxllncColor.opDark80,
    color: xxllncColor.opLight60,
    borderRadius: border.radius.lg,
    fontSize: 9,
    fontWeight: 700,
    marginTop: '14px',
    padding: '5px 8px',
  },
  [`& .${classes.spacer}`]: {
    height: '80px',
  },  
  [`& .${classes.footer}`]: {
    textAlign: 'center',
    color: '#fff',
    position: 'absolute',
    bottom: 10,
    width: '100%',
    left: 0,
    '& ul': {
      listStyleType: 'none',
      padding: 0,
      margin: 0,
    },
    '& li': {
      color: '#fff',
      fontSize: '0.9rem',
    },
    '& a': {
      color: '#fff',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
}))

const StyledMenuItemLink = styled(MenuItemLink)(({ theme }) => ({
  '&.RaMenuItemLink-active': {
    backgroundColor: 'unset',
    color: xxllncColor.dark100,
  },
}))

export default Menu